/**
 * The rules around deleting an account, kept out of the component so they can be
 * tested — and so the two places that need them (which button a row gets, and
 * whether the confirm dialog's Delete is armed) can't drift apart.
 *
 * Every rule here is also enforced by the server (jcseo/api/admin_users.py).
 * This is not the gate; it's the UI declining to offer a button that would be
 * refused, and declining to arm one whose confirmation doesn't match.
 */
import type { AdminUser, AdminUserDeletionPreview } from '../types';

/**
 * Delete is offered on every status — a stale approved account is as deletable
 * as a stray signup — but never for yourself and never for another admin. The
 * server refuses both, so offering them would only produce an error.
 */
export function canOfferDelete(user: AdminUser, currentUserId?: number): boolean {
  return user.id !== currentUserId && !user.is_admin;
}

/**
 * The typed confirmation. Case and surrounding whitespace are forgiven because
 * neither distinguishes one account from another — the point is that the admin
 * looked at WHICH account they are deleting, not that they can type exactly.
 * The server applies the same comparison.
 */
export function confirmationMatches(typed: string, email: string): boolean {
  return typed.trim().toLowerCase() === email.trim().toLowerCase();
}

/**
 * Whether the dialog's Delete button should be live. All four conditions have to
 * hold: the preview has loaded (so nobody deletes blind), nothing RESTRICTs the
 * delete, the confirmation matches, and no request is already in flight.
 */
export function canSubmitDelete(args: {
  preview: AdminUserDeletionPreview | null;
  typed: string;
  deleting: boolean;
}): boolean {
  const { preview, typed, deleting } = args;
  if (!preview || !preview.deletable || deleting) return false;
  return confirmationMatches(typed, preview.email);
}
